import {Component, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {SubscribedComponent} from '../../subscribed.component';
import {InvoicingCompanyComponent} from '../invoicing-company/invoicing-company.component';
import {VatOptionsComponent} from '../vat-options/vat-options.component';
import {KmCostTypeComponent} from '../km-cost-type/km-cost-type.component';
import {ReimbursementRatesComponent} from '../reimbursement-rates/reimbursement-rates.component';
import {QuoteSettingsComponent} from '../quote-settings/quote-settings.component';
import {COMPANY_TABS, CompanyTab, CompanyTabsService, FIELD_LABELS, SECTION_FIELDS, companyTabOf, groupMissing} from './company-tabs';

/** One routed My company tab (PR7): the readiness summary, then the editors this tab owns. */
@Component({
  selector: 'bst-company-tab',
  templateUrl: './company-tab.component.html',
  styleUrls: ['../clients.component.scss', '../invoicing-client/invoicing-form.scss'],
  standalone: false
})
export class CompanyTabComponent extends SubscribedComponent {
  @ViewChild(InvoicingCompanyComponent) company: InvoicingCompanyComponent;
  @ViewChild(VatOptionsComponent) vat: VatOptionsComponent;
  @ViewChild(KmCostTypeComponent) km: KmCostTypeComponent;
  @ViewChild(ReimbursementRatesComponent) rates: ReimbursementRatesComponent;
  @ViewChild(QuoteSettingsComponent) quotes: QuoteSettingsComponent;
  tab: CompanyTab;
  missing: string[] | null = null;
  readonly labels = FIELD_LABELS;

  constructor(private route: ActivatedRoute, private companyTabs: CompanyTabsService) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.tab = this.route.snapshot.data.tab;
    this.subs.push(this.companyTabs.missing$.subscribe(missing => this.missing = missing));

    // A deep link from an invoice issue (?field=iban): focus$ cannot cross this navigation
    // (it is reset on route change, see CompanyTabsService), so read it here instead.
    const field = this.route.snapshot.queryParamMap.get('field');
    if (field && companyTabOf('company.' + field) === this.tab) {
      this.companyTabs.focus$.next(field);
    }
  }

  // One row per tab with missing fields, in form order; keys no tab owns are dropped, so every
  // link has a target and the rows agree with the tab counts.
  get rows(): {tab: CompanyTab, fields: string[]}[] {
    const groups = groupMissing(this.missing ?? []);
    return COMPANY_TABS.filter(t => groups[t].length).map(t => ({
      tab: t, fields: SECTION_FIELDS[t].filter(f => groups[t].includes('company.' + f)),
    }));
  }

  // The editor that owns the field focuses it once it has loaded, here or on the tab the link opens.
  open(field: string) {
    this.companyTabs.focus$.next(field);
  }

  get isDirty(): boolean {
    return (this.tab === 'profile' && this.companyTabs.shellDirty())
      || [this.company, this.vat, this.km, this.rates, this.quotes].some(c => !!c?.isDirty);
  }
}
